/** Exact decimal/fraction equivalence. Rounded values must be explicitly accepted.
 * No evaluation, scientific notation, implicit blank-as-zero, or loose tolerance.
 */
function decimal(value: string): [bigint, bigint] | null {
  value = value.replace(/^([+-])\s+/, "$1");
  if (!/^[+-]?(?:(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d*)?|\.\d+)$/.test(value)) return null;
  const clean = value.replaceAll(",", "");
  const places = clean.includes(".") ? clean.length - clean.indexOf(".") - 1 : 0;
  return [BigInt(clean.replace(".", "")), 10n ** BigInt(places)];
}
function rational(value: string): [bigint, bigint] | null {
  if (!value || value.length > 500) return null;
  const parts = value.replaceAll("−", "-").split("/");
  if (parts.length > 2) return null;
  const a = decimal(parts[0].trim());
  if (!a) return null;
  if (parts.length === 1) return a;
  const b = decimal(parts[1].trim());
  return b && b[0] !== 0n ? [a[0] * b[1], a[1] * b[0]] : null;
}
export function equivalentNumericResponse(response: string, accepted: string): boolean {
  if (accepted.includes("|")) {
    const alternatives = accepted.split("|").map((value) => value.trim());
    return alternatives.every((value) => rational(value) !== null) && alternatives.some((value) => equivalentNumericResponse(response, value));
  }
  const a = rational(response.trim());
  const b = rational(accepted.trim());
  if (a && b) return a[0] * b[1] === b[0] * a[1];
  // A few reviewed bank entries explicitly accept symbolic expressions. Preserve
  // exact text matching for these; never treat invalid numeric syntax as a number.
  return !a && !b && /sqrt|√/.test(accepted) && response.trim().toLowerCase() === accepted.trim().toLowerCase();
}

/** Bluebook's five-character number body, plus one optional leading minus. */
export function numericResponseLimit(value: string): number {
  return value.startsWith("-") || value.startsWith("−") ? 6 : 5;
}
export function normalizeNumericMinus(value: string): string {
  return value.replaceAll("−", "-");
}
/** Incomplete entries are valid drafts, so typing '-' or '2/' cannot block autosave. */
export function numericDraftError(value: string): string | null {
  value = normalizeNumericMinus(value);
  if (value.length > numericResponseLimit(value)) return "Use at most 5 characters, or 6 including a leading minus sign.";
  if (/[^0-9./-]/.test(value)) return "Use digits, a decimal point or fraction bar, and an optional leading minus. No spaces, mixed numbers, commas, units or other symbols.";
  if (!/^-?(?:\d*(?:\.\d*)?|\d+\/\d*)$/.test(value)) return "Use one decimal point or one fraction bar. A minus sign belongs only at the beginning.";
  return null;
}
export function isSatNumericResponse(value: string): boolean {
  value = normalizeNumericMinus(value);
  return !numericDraftError(value) && /^-?(?:\d+(?:\.\d*)?|\.\d+|\d+\/\d+)$/.test(value) && rational(value) !== null;
}
export function numericResponseError(value: string): string | null {
  if (!value) return null;
  const draftError = numericDraftError(value);
  if (draftError) return draftError;
  if (/\/0+$/.test(value)) return "A fraction's denominator cannot be zero.";
  return isSatNumericResponse(value) ? null : "Finish entering the number or fraction.";
}

function sameValue(a: [bigint, bigint], b: [bigint, bigint]): boolean {
  return a[0] * b[1] === b[0] * a[1];
}

/** Generate the rounded/truncated values that fit, with and without a leading zero.
 * Compare exact rationals, never an epsilon: 2/3 accepts .6666/.6667/0.666/0.667,
 * but not .66/.67. Numeric equivalents of those encodings also receive credit.
 */
function fittingDecimals([numerator, denominator]: [bigint, bigint]): string[] {
  const negative = (numerator < 0n) !== (denominator < 0n);
  const n = numerator < 0n ? -numerator : numerator;
  const d = denominator < 0n ? -denominator : denominator;
  const whole = n / d;
  const values: string[] = [];
  for (const omitZero of whole === 0n ? [false, true] : [false]) {
    const wholeLength = omitZero ? 0 : whole.toString().length;
    const places = 5 - wholeLength - 1;
    if (places < 0) continue;
    const scale = 10n ** BigInt(places);
    const truncated = n * scale / d;
    const remainder = n * scale % d;
    const rounded = truncated + (remainder * 2n >= d ? 1n : 0n);
    for (const value of [truncated, rounded]) {
      let text = places ? `${value / scale}.${(value % scale).toString().padStart(places, "0")}` : value.toString();
      if (text.includes(".")) text = text.replace(/0+$/, "").replace(/\.$/, "");
      if (omitZero && text.startsWith("0.")) text = text.slice(1);
      if (negative) text = `-${text}`;
      if (isSatNumericResponse(text)) values.push(text);
    }
  }
  return [...new Set(values)];
}

/** Safe entry examples for reviewing a key whose exact value may exceed the field. */
export function satNumericResponseExamples(accepted: string): string[] {
  const alternatives = accepted.split("|").map(value => normalizeNumericMinus(value.trim()));
  const keys = alternatives.map(rational);
  if (keys.some(key => key === null)) return [];
  return [...new Set(alternatives.flatMap((value, i) => [
    ...(isSatNumericResponse(value) ? [value] : []), ...fittingDecimals(keys[i]!)
  ]))];
}

/** Learner SAT scoring. Keys may contain exact longer numbers or fractions;
 * only the student's entry must obey the Bluebook entry rules. */
export function matchesSatNumericResponse(response: string, accepted: string): boolean {
  if (!isSatNumericResponse(response)) return false;
  const answer = rational(normalizeNumericMinus(response))!;
  const keys = accepted.split("|").map(value => rational(value.trim()));
  if (!keys.length || keys.some(key => key === null)) return false;
  return keys.some(key => sameValue(answer, key!) || fittingDecimals(key!).some(value => sameValue(answer, rational(value)!)));
}
